import React, { useCallback, useEffect, useState } from 'react';
import '../styles/ReportModal.css';
import { FileSpreadsheet, GraduationCap, LayoutGrid, Users } from "lucide-react";

const API = process.env.REACT_APP_API_URL;

export default function ReportModal({ isOpen, onClose }) {
  const [groups, setGroups] = useState([]);
  const [modules, setModules] = useState([]);
  const [lecturers, setLecturers] = useState([]);

  const [groupsLoading, setGroupsLoading] = useState(false);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [lecturersLoading, setLecturersLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    type: 'general',
    period: 'last-week',
    format: 'xlsx',
    group: '',
    module: '',
    lecturer: ''
  });

  const fetchModulesForGroup = useCallback(async (groupCode) => {
    if (!groupCode) {
      setModules([]);
      return;
    }
    setModulesLoading(true);
    try {
      const q = new URLSearchParams({ group_code: groupCode });
      const response = await fetch(`${API}/reports/filters/modules?${q}`);
      if (!response.ok) throw new Error('modules');
      const data = await response.json();
      setModules(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching modules:', error);
      setModules([]);
    } finally {
      setModulesLoading(false);
    }
  }, []);

  const fetchLecturersForGroup = useCallback(async (groupCode, moduleId) => {
    if (!groupCode) {
      setLecturers([]);
      return;
    }
    setLecturersLoading(true);
    try {
      const q = new URLSearchParams({ group_code: groupCode });
      if (moduleId) q.set('module_id', String(moduleId));
      const response = await fetch(`${API}/reports/filters/lecturers?${q}`);
      if (!response.ok) throw new Error('lecturers');
      const data = await response.json();
      setLecturers(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching lecturers:', error);
      setLecturers([]);
    } finally {
      setLecturersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    setFormData({
      type: 'general',
      period: 'last-week',
      format: 'xlsx',
      group: '',
      module: '',
      lecturer: ''
    });
    setModules([]);
    setLecturers([]);

    let cancelled = false;
    (async () => {
      setGroupsLoading(true);
      try {
        const response = await fetch(`${API}/reports/groups`);
        const data = await response.json();
        if (!cancelled) setGroups(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Error fetching groups:', error);
        if (!cancelled) setGroups([]);
      } finally {
        if (!cancelled) setGroupsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !formData.group || formData.type === 'general') return;
    fetchModulesForGroup(formData.group);
    fetchLecturersForGroup(formData.group, formData.module || null);
  }, [isOpen, formData.type, formData.group, formData.module, fetchModulesForGroup, fetchLecturersForGroup]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleGroupChange = (e) => {
    const group = e.target.value;
    setFormData(prev => ({ ...prev, group, module: '', lecturer: '' }));
    setModules([]);
    setLecturers([]);
  };

  const handleModuleChange = (e) => {
    const module = e.target.value;
    setFormData(prev => ({ ...prev, module, lecturer: '' }));
  };

  const handleGenerateReport = async () => {
    if (!formData.group) {
      alert('Proszę wybrać grupę (kurs).');
      return;
    }
    if (formData.type !== 'general' && !formData.lecturer) {
      alert('Proszę wybrać wykładowcę.');
      return;
    }

    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      if (!token) {
        setSubmitting(false);
        return;
      }

      const payload = {
        type: formData.type,
        period: formData.period,
        format: formData.format,
        group: formData.group
      };
      if (formData.type !== 'general') {
        payload.lecturer = formData.lecturer;
        if (formData.module) {
          payload.moduleId = parseInt(formData.module, 10);
        }
      }

      const response = await fetch(`${API}/reports/report`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        console.error('Error generating report:', error);
        alert(error.error || 'Nie udało się wygenerować raportu.');
        return;
      }

      await response.json();
      onClose();
    } catch (error) {
      console.error('Błąd podczas generowania raportu:', error);
      alert('Wystąpił błąd podczas generowania raportu. Spróbuj ponownie.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const reportTypes = [
    {
      id: 'general',
      name: 'Raport ogólny',
      description: 'Dziennik zajęć: obecności i oceny dla całej grupy (kursu)',
      icon: LayoutGrid,
      color: '#0d9488',
      formats: ['PDF', 'Excel']
    },
    {
      id: 'attendance',
      name: 'Raport obecności',
      description: 'Frekwencja uczniów w wybranym okresie',
      icon: Users,
      color: '#16a34a',
      formats: ['PDF', 'Excel']
    },
    {
      id: 'grades',
      name: 'Raport ocen',
      description: 'Zestawienie ocen i średnich',
      icon: GraduationCap,
      color: '#3b82f6',
      formats: ['PDF', 'Excel']
    }
  ];

  const isGeneralReport = formData.type === 'general';

  const groupSelected = Boolean(formData.group);
  const moduleSelectDisabled = isGeneralReport || !groupSelected || modulesLoading;
  const lecturerSelectDisabled = isGeneralReport || !groupSelected || lecturersLoading;

  return (
    <div className="report-modal">
      <div className="report-modal-holder">
        <div className="report-modal-card">
          <h3 className="report-modal-title">
            <FileSpreadsheet size={20} color="#16a34a" />
            Generator Raportów
          </h3>
          <div className="report-modal-types">
            {reportTypes.map(type => (
              <div
                key={type.id}
                className={`report-modal-type-card ${formData.type === type.id ? 'report-modal-type-card-active' : ''}`}
                onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
              >
                <div className='report-modal-type-icon' style={{ backgroundColor: type.color }}>
                  <type.icon size={24} />
                </div>
                <h4 className='report-modal-type-name'>{type.name}</h4>
                <p className='report-modal-type-description'>{type.description}</p>
                <div className='report-modal-type-formats'>
                  {type.formats.map(format => (
                    <span
                      key={format}
                      className='report-modal-type-format'
                    >
                      {format}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <form className="report-modal-form" onSubmit={(e) => e.preventDefault()}>
            <div className='report-modal-form-group'>
              <label className='report-modal-form-label'>Wybierz okres:</label>
              <select
                className='report-modal-form-select'
                name="period"
                value={formData.period}
                onChange={handleInputChange}
              >
                <option value="last-week">Ostatni tydzień</option>
                <option value="last-month">Ostatni miesiąc</option>
                <option value="last-quarter">Ostatni kwartał</option>
                <option value="last-year">Ostatni rok</option>
              </select>
            </div>
            <div className='report-modal-form-group'>
              <label className='report-modal-form-label'>Format eksportu</label>
              <select
                className='report-modal-form-select'
                name="format"
                value={formData.format}
                onChange={handleInputChange}
              >
                <option value="xlsx">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>

            <div className='report-modal-form-group'>
              <label className='report-modal-form-label'>
                {isGeneralReport ? 'Wybierz grupę (kurs)' : '1. Wybierz grupę'}
              </label>
              <select
                className='report-modal-form-select'
                name="group"
                value={formData.group}
                onChange={handleGroupChange}
                disabled={groupsLoading}
              >
                <option value="">{groupsLoading ? 'Ładowanie grup…' : 'Wybierz grupę…'}</option>
                {groups.map(group => (
                  <option key={group.group_code} value={group.group_code}>
                    {group.group_name}
                  </option>
                ))}
              </select>
              {isGeneralReport && (
                <p className="report-modal-field-hint">
                  Raport ogólny obejmuje cały kurs — wszystkie zajęcia grupy w wybranym okresie (bez wyboru wykładowcy).
                </p>
              )}
            </div>

            {!isGeneralReport && (
              <>
                <div className='report-modal-form-group'>
                  <label className='report-modal-form-label'>2. Przedmiot (opcjonalnie)</label>
                  <select
                    className='report-modal-form-select'
                    name="module"
                    value={formData.module}
                    onChange={handleModuleChange}
                    disabled={moduleSelectDisabled}
                  >
                    <option value="">Wszystkie przedmioty w grupie</option>
                    {modules.map(m => (
                      <option key={m.module_id} value={String(m.module_id)}>
                        {m.module_name}
                      </option>
                    ))}
                  </select>
                  {groupSelected && modulesLoading && (
                    <p className="report-modal-field-hint">Ładowanie przedmiotów…</p>
                  )}
                </div>

                <div className='report-modal-form-group'>
                  <label className='report-modal-form-label'>3. Wybierz wykładowcę</label>
                  <select
                    className='report-modal-form-select'
                    name="lecturer"
                    value={formData.lecturer}
                    onChange={handleInputChange}
                    disabled={lecturerSelectDisabled}
                  >
                    <option value="">
                      {!groupSelected
                        ? 'Najpierw wybierz grupę'
                        : lecturersLoading
                          ? 'Ładowanie wykładowców…'
                          : 'Wybierz wykładowcę…'}
                    </option>
                    {lecturers.map(lecturer => (
                      <option key={lecturer.lecturer_id} value={lecturer.lecturer_id}>
                        {lecturer.first_name} {lecturer.last_name}
                      </option>
                    ))}
                  </select>
                  {groupSelected && !lecturersLoading && lecturers.length === 0 && (
                    <p className="report-modal-field-hint">
                      Brak wykładowców przypisanych do tej grupy (sprawdź powiązania modułów z grupą).
                    </p>
                  )}
                </div>
              </>
            )}
          </form>
          <div className="report-modal-buttons">
            <button type="button" className="report-modal-button report-modal-button-secondary" onClick={onClose} disabled={submitting}>
              Anuluj
            </button>
            <button
              type='button'
              className="report-modal-button report-modal-button-primary"
              onClick={handleGenerateReport}
              disabled={submitting || groupsLoading}
            >
              {submitting ? 'Generowanie…' : 'Generuj raport'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
